import React from 'react';
import { Link } from 'react-router-dom';
import { format, differenceInDays } from 'date-fns';
import {
  Tv, Hash, MonitorPlay, Clapperboard, Trophy, Newspaper, Baby, Globe2, Languages, Plus,
  Calendar, MonitorSpeaker, CreditCard, SlidersHorizontal, Gift, FileText,
} from 'lucide-react';
import SupportQuickAction from './SupportQuickAction';
import PlanPdfQuickAction from './PlanPdfQuickAction';
import RetrackStbQuickAction from './RetrackStbQuickAction';

const fmt = n => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));

const GENRES = [
  ['sports', 'Sports', Trophy],
  ['movies', 'Movies', Clapperboard],
  ['news', 'News', Newspaper],
  ['kids', 'Kids', Baby],
  ['regional', 'Regional', Languages],
  ['international', 'International', Globe2],
];

const QUICK_ACTIONS = [
  { icon: CreditCard, label: 'Recharge', to: '/portal/payments', color: '#2563eb' },
  { icon: SlidersHorizontal, label: 'Change Pack', to: '/portal/plans?service=catv', color: '#7c3aed' },
  { icon: FileText, label: 'View Invoice', to: '/portal/payments', color: '#0d9488' },
  { icon: Gift, label: 'Refer & Earn', to: '/portal/refer', color: '#d97706' },
];

// Card for the CableTV service — connection ID, a Plan/Valid-Till/Pack stat row,
// expiry countdown with a validity progress bar, STB serial, channel/genre chips,
// add-on packages, and its own Quick Actions row.
export default function CatvHero({ data, connectionId, durationType, status, serviceType }) {
  if (!data) return null;
  const pkg = data.package;
  // `catv.isActive` is an internal renewal/service-request bookkeeping flag (defaults
  // false, only flips true on a collected renewal payment or an approved reconnection
  // request) — it does NOT reliably reflect whether the customer's connection is active,
  // e.g. customers created/imported without ever tripping those code paths stay stuck
  // false despite a valid package + unexpired date. The account-level `status` (what
  // staff actually toggle on CustomerDetailPage) is the real signal for this badge.
  const isActive = status ? status === 'active' : data.isActive !== false;
  const daysLeft = data.expiryDate ? differenceInDays(new Date(data.expiryDate), new Date()) : null;
  const expired = daysLeft !== null && daysLeft < 0;
  // Regular (durationType 'regular'/unset) customers are renewed automatically by the
  // nightly cron, so unlike DTD there's no urgency-driven window — the button is just
  // always offered as an optional way to prepay ahead of time and lock in the advance
  // discount tiers (2026-08-01, see CustomerRenewPage.js).
  const isDtd = durationType === 'dtd';
  const showRenew = isDtd ? (expired || (daysLeft !== null && daysLeft <= 5)) : true;
  const validityDays = pkg?.validityDays || 30;
  const leftPct = daysLeft === null ? null : Math.max(0, Math.min(1, daysLeft / validityDays));

  const addOns = (data.additionalPackages || []).filter(a => a.package);
  const monthlyTotal = (pkg?.price || 0) + addOns.reduce((s, a) => s + (a.price ?? a.package?.price ?? 0), 0);
  const genres = GENRES.filter(([key]) => pkg?.includes?.[key]);

  const chips = [];
  if (pkg?.channelCount) chips.push(`${pkg.channelCount} Channels`);
  if (pkg?.hdChannelCount) chips.push(`${pkg.hdChannelCount} HD`);
  if (pkg?.hasIPTV) chips.push(pkg.iptvChannelCount ? `${pkg.iptvChannelCount} IPTV` : 'IPTV');

  return (
    <div className="card" style={{ marginBottom: 20, padding: '18px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <div style={{ fontSize: '1.05rem', fontWeight: 800 }}>CableTV Connection</div>
          {(data.accountNumber || connectionId) && (
            <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginTop: 2 }}>
              Connection ID: {data.accountNumber || connectionId}
            </div>
          )}
        </div>
        <span className={`badge badge-${isActive ? 'success' : 'danger'}`} style={{ flexShrink: 0 }}>
          {isActive ? 'Active' : 'Inactive'}
        </span>
      </div>

      {/* Plan / Valid Till / Pack stat row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 16 }}>
        <div>
          <div style={{
            width: 30, height: 30, borderRadius: 9, background: 'var(--primary-light)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6,
          }}>
            <Tv size={15} color="var(--primary)" />
          </div>
          <div style={{ fontSize: '.68rem', color: 'var(--text-muted)' }}>Plan</div>
          <div style={{ fontWeight: 700, fontSize: '.85rem' }}>{pkg?.displayName || pkg?.name || '—'}</div>
          {!!pkg?.channelCount && <div style={{ fontSize: '.7rem', color: 'var(--text-muted)' }}>{pkg.channelCount}+ Channels</div>}
        </div>
        <div>
          <div style={{
            width: 30, height: 30, borderRadius: 9, background: '#ede9fe',
            display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6,
          }}>
            <Calendar size={15} color="#7c3aed" />
          </div>
          <div style={{ fontSize: '.68rem', color: 'var(--text-muted)' }}>Valid Till</div>
          <div style={{ fontWeight: 700, fontSize: '.85rem' }}>{data.expiryDate ? format(new Date(data.expiryDate), 'dd MMM yyyy') : '—'}</div>
          {daysLeft !== null && (
            <div style={{ fontSize: '.7rem', color: expired ? '#dc2626' : 'var(--text-muted)' }}>
              {expired ? `Expired ${Math.abs(daysLeft)}d ago` : `${daysLeft} Days Left`}
            </div>
          )}
        </div>
        <div>
          <div style={{
            width: 30, height: 30, borderRadius: 9, background: '#dcfce7',
            display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6,
          }}>
            <MonitorSpeaker size={15} color="#16a34a" />
          </div>
          <div style={{ fontSize: '.68rem', color: 'var(--text-muted)' }}>Service Type</div>
          <div style={{ fontWeight: 700, fontSize: '.85rem' }}>{serviceType || '—'}</div>
          <div style={{ fontSize: '.7rem', color: 'var(--text-muted)' }}>{addOns.length > 0 ? `+${addOns.length} add-on${addOns.length > 1 ? 's' : ''}` : ' '}</div>
        </div>
      </div>

      {leftPct !== null && (
        <div style={{ marginTop: 14 }}>
          <div style={{ height: 6, borderRadius: 99, background: 'var(--border)', overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${Math.round(leftPct * 100)}%`, borderRadius: 99,
              background: expired || daysLeft <= 3 ? '#ef4444' : daysLeft <= 7 ? '#f59e0b' : '#22c55e',
              transition: 'width .6s ease',
            }} />
          </div>
        </div>
      )}

      {data.setTopBoxSerial && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.8rem', color: 'var(--text-muted)', minWidth: 0 }}>
            <Hash size={12} />
            Set-Top Box:
            <span className="font-mono" style={{ color: 'var(--text)', fontWeight: 700, wordBreak: 'break-all' }}>{data.setTopBoxSerial}</span>
          </div>
          <RetrackStbQuickAction inline />
        </div>
      )}

      {(chips.length > 0 || genres.length > 0) && (
        <>
          <div style={{ fontSize: '.66rem', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-muted)', marginTop: 16, marginBottom: 6 }}>
            Included With Your Plan
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {chips.map(c => (
              <span key={c} style={{
                display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '.72rem', fontWeight: 700,
                padding: '4px 11px', borderRadius: 99, background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)',
              }}>
                <MonitorPlay size={11} /> {c}
              </span>
            ))}
            {genres.map(([key, label, Icon]) => (
              <span key={key} style={{
                display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '.72rem', fontWeight: 600,
                padding: '4px 11px', borderRadius: 99, background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-muted)',
              }}>
                <Icon size={11} /> {label}
              </span>
            ))}
          </div>
        </>
      )}

      {addOns.length > 0 && (
        <div style={{ marginTop: 16, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 12, padding: '10px 14px' }}>
          <div style={{ fontSize: '.66rem', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 6 }}>
            Add-on Packs
          </div>
          {addOns.map((a, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '.83rem', padding: '3px 0' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                <Plus size={11} color="var(--text-muted)" />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.package.displayName || a.package.name}</span>
              </span>
              <span style={{ fontWeight: 700, flexShrink: 0 }}>₹{fmt(a.price ?? a.package.price)}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '.85rem', paddingTop: 7, marginTop: 5, borderTop: '1px solid var(--border)' }}>
            <span style={{ fontWeight: 600, color: 'var(--text-muted)' }}>Monthly Total</span>
            <span style={{ fontWeight: 800, fontSize: '1rem' }}>₹{fmt(monthlyTotal)}</span>
          </div>
        </div>
      )}

      {showRenew && (
        // DTD "Renew Now" opens the Recharge screen itself (owner, 2026-09-28) so both show the
        // same 3/6/12-month options, pay-5-get-6 / pay-10-get-12 prices and package picker.
        <Link to={isDtd ? '/portal/payments' : '/portal/renew?service=catv'} className="btn btn-primary" style={{
          marginTop: 16, width: '100%', justifyContent: 'center', padding: '11px 0',
        }}>
          {isDtd ? 'Renew Now' : 'Recharge in Advance'}
        </Link>
      )}
      {!isDtd && (
        <div style={{ marginTop: showRenew ? 8 : 16, padding: '10px 14px', borderRadius: 10, background: 'var(--bg)', border: '1px solid var(--border)', fontSize: '.8rem', color: 'var(--text-muted)', textAlign: 'center' }}>
          Auto-renews monthly — no action needed{!expired ? ', recharge above only to lock in advance savings' : ''}.
        </div>
      )}

      {/* Quick Actions */}
      <div style={{ fontSize: '.66rem', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-muted)', marginTop: 18, marginBottom: 8 }}>
        Quick Actions
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: 10 }}>
        {QUICK_ACTIONS.map(a => (
          <Link key={a.label} to={a.to} style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '12px 6px',
            textDecoration: 'none', border: '1px solid var(--border)', borderRadius: 12,
          }}>
            <div style={{
              width: 34, height: 34, borderRadius: 10, background: `${a.color}14`, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}>
              <a.icon size={16} color={a.color} />
            </div>
            <span style={{ fontSize: '.72rem', fontWeight: 600, color: 'var(--text)', textAlign: 'center' }}>{a.label}</span>
          </Link>
        ))}
        <SupportQuickAction />
        <PlanPdfQuickAction url={pkg?.pdf} />
      </div>
    </div>
  );
}
